import React from "react";
import { useTranslation } from "react-i18next";

const TherapistCalendarGrid = ({ 
  currentYear, 
  currentMonth, 
  selectedDate, 
  setSelectedDate, 
  appointments,
  setAppointments,
  handleAddAvailability,
  handleDateClick,
  availabilitySlots // Müsaitlik bilgilerini alacağız
}) => {
  const { t } = useTranslation();
  const today = new Date();
  const todayDate = today.getDate();
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const normalizeStatus = (status) => (status || "").toLowerCase();
  const isPendingStatus = (status) => normalizeStatus(status) === "pending";
  const isConfirmedStatus = (status) => ["confirmed", "accepted", "completed"].includes(normalizeStatus(status));
  const isCancelledStatus = (status) => ["cancelled", "canceled", "rejected"].includes(normalizeStatus(status));

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const startDay = new Date(currentYear, currentMonth, 1).getDay();
  const prevMonthDays = new Date(currentYear, currentMonth, 0).getDate();

  // Belirli bir gün için müsaitlik kontrolü
  const hasAvailability = (day) => {
    if (!availabilitySlots || !Array.isArray(availabilitySlots)) return false;
    
    return availabilitySlots.some(slot => 
      slot.day === day && 
      slot.month === currentMonth && 
      slot.year === currentYear
    );
  };

  // Gün arka plan rengini belirleme
  const getDayBackgroundColor = (day) => {
    const dateKey = `${day}-${currentMonth}-${currentYear}`;
    const hasAppointments = appointments[dateKey] && appointments[dateKey].length > 0;
    const dayHasAvailability = hasAvailability(day);
    
    if (day === selectedDate) {
      return "bg-primary text-white";
    }
    
    if (day === todayDate && currentMonth === todayMonth && currentYear === todayYear) {
      return "bg-background";
    }

    if (hasAppointments) {
      // Randevu durumlarına göre renklendirme
      const pendingCount = appointments[dateKey].filter(appt => isPendingStatus(appt.status)).length;
      const confirmedCount = appointments[dateKey].filter(appt => isConfirmedStatus(appt.status)).length;
      
      if (pendingCount > 0 && confirmedCount > 0) {
        return "bg-yellow-100"; // Hem bekleyen hem onaylanmış randevular
      } else if (pendingCount > 0) {
        return "bg-orange-100"; // Bekleyen randevular
      } else if (confirmedCount > 0) {
        return "bg-green-100"; // Onaylanmış randevular
      }
      
      return "bg-gray-100"; // Diğer durumdaki randevular
    }
    
    // Eğer sadece müsaitlik varsa ve randevu yoksa
    if (dayHasAvailability) {
      return "bg-emerald-50"; // Müsait zamanlar için açık yeşil
    }
    
    return "";
  };

  // Randevu ve müsaitlik sayılarını gösteren indicators
  const renderDayIndicators = (day) => {
    const dateKey = `${day}-${currentMonth}-${currentYear}`;
    const dayAppointments = appointments[dateKey];
    
    // Müsaitlik sayısını hesapla
    const availabilityCount = availabilitySlots ? availabilitySlots.filter(slot => 
      slot.day === day && 
      slot.month === currentMonth && 
      slot.year === currentYear
    ).length : 0;
    
    // Randevu ve müsaitlik yoksa gösterge gösterme
    if ((!dayAppointments || dayAppointments.length === 0) && availabilityCount === 0) {
      return null;
    }

    // Durumlara göre randevu sayılarını grupla
    const pendingCount = dayAppointments ? dayAppointments.filter(appt => isPendingStatus(appt.status)).length : 0;
    const confirmedCount = dayAppointments ? dayAppointments.filter(appt => isConfirmedStatus(appt.status)).length : 0;
    const cancelledCount = dayAppointments ? dayAppointments.filter(appt => isCancelledStatus(appt.status)).length : 0;
    
    return (
      <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1 items-center">
        {pendingCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <div className="text-[8px] text-orange-700 leading-tight">{pendingCount}</div>
          </div>
        )}
        
        {confirmedCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <div className="text-[8px] text-green-700 leading-tight">{confirmedCount}</div>
          </div>
        )}
        
        {cancelledCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
            <div className="text-[8px] text-gray-700 leading-tight">{cancelledCount}</div>
          </div>
        )}
        
        {availabilityCount > 0 && (
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
            <div className="text-[8px] text-emerald-700 leading-tight">{availabilityCount}</div>
          </div>
        )}
      </div>
    );
  };

  // Takvim gününe tıklama
  const onDayClick = (day) => {
    setSelectedDate(day);
    if (handleDateClick) {
      handleDateClick(day, currentMonth, currentYear);
    }
  };

  // Takvim gününde sağ tıklama
  const onDayRightClick = (e, day) => {
    e.preventDefault();
    if (handleAddAvailability) {
      handleAddAvailability(day, currentMonth, currentYear);
    }
    return false;
  };

  // Geçmiş günlere müsaitlik eklenmemesi için kontrol
  const isPastDay = (day) => {
    const currentDate = new Date();
    const checkDate = new Date(currentYear, currentMonth, day);
    return checkDate < new Date(currentDate.setHours(0, 0, 0, 0));
  };

  return (
    <div className="grid grid-cols-7 gap-0 text-center font-medium border-t border-l flex-grow">
      {/* Hafta günleri başlıkları */}
      {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
        <div key={day} className="py-3 border-b border-r font-semibold bg-gray-50">
          {t(day)}
        </div>
      ))}

      {/* Önceki ayın günleri */}
      {Array.from({ length: startDay }, (_, i) => prevMonthDays - startDay + i + 1).map((day) => (
        <div key={`prev-${day}`} className="py-3 border-b border-r text-gray-400 bg-gray-50">
          {day}
        </div>
      ))}

      {/* Mevcut ayın günleri */}
      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
        const dayIsPast = isPastDay(day);
        
        return (
          <button
            key={day}
            className={`
              py-3 border-b border-r text-lg font-semibold w-full transition relative
              ${getDayBackgroundColor(day)}
              ${dayIsPast ? 'cursor-default opacity-60' : 'hover:bg-gray-100 hover:opacity-90'}
            `}
            onClick={() => onDayClick(day)}
            onContextMenu={(e) => dayIsPast ? e.preventDefault() : onDayRightClick(e, day)}
            title={dayIsPast ? t("cannotAddAvailabilityForPastDays") : t("rightClickToAddAvailability")}
            disabled={dayIsPast}
          >
            {day}
            {renderDayIndicators(day)}
          </button>
        );
      })}

      {/* Sonraki ayın günleri */}
      {Array.from({ length: 42 - (startDay + daysInMonth) }, (_, i) => i + 1).map((day) => (
        <div key={`next-${day}`} className="py-3 border-b border-r text-gray-400 bg-gray-50">
          {day}
        </div>
      ))}
    </div>
  );
};

export default TherapistCalendarGrid;
